import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { formatINR } from '@new-wealth/shared';
import { api } from '../api/client.js';
import { DataTable, type Column } from '../components/DataTable.js';
import { CustomerProfileModal } from '../components/CustomerProfileModal.js';

type Seg = 'series' | 'customer' | 'district' | 'branch' | 'agent' | 'staff' | 'lockerhub' | 'dhanamfin';
const TABS: { key: Seg; label: string }[] = [
  { key: 'series', label: 'Series-wise' },
  { key: 'customer', label: 'Customer-wise' },
  { key: 'district', label: 'District-wise' },
  { key: 'branch', label: 'Branch-wise' },
  { key: 'agent', label: 'Agent-wise' },
  { key: 'staff', label: 'Staff-wise' },
  { key: 'lockerhub', label: 'Locker Deposit' },
  { key: 'dhanamfin', label: 'Dhanamfin App' },
];
const TAB_KEYS = TABS.map((t) => t.key);

interface Child {
  application_no: string; customer_id: number; customer: string; customer_code: string;
  series_code: string; amount: string; outstanding: string; redeemed: string; status: string; allotment_date: string | null;
  date_money_received: string | null;
  /** Who brought the investment — the Branch-wise tab's "Brought by" column. */
  sourced_by: string;
}
interface Group {
  key: string; label: string; sublabel: string | null; district: string | null; sourced_by: string | null;
  investors: number; investments: number; outstanding: string; children: Child[];
  window_from?: string | null; window_to?: string | null; issued?: string; redeemed?: string;
}

/** "Jun 2026 – Jul 2026" (or a single month) from the collection window dates. */
function fmtWindow(from?: string | null, to?: string | null): string {
  if (!from && !to) return '—';
  const m = (d: string) => new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
  const a = from ? m(from) : '—';
  const b = to ? m(to) : '—';
  return a === b ? a : `${a} – ${b}`;
}

export function SegmentsPage() {
  const [params, setParams] = useSearchParams();
  const paramTab = params.get('tab') as Seg | null;
  const openKey = params.get('open');
  const [tab, setTab] = useState<Seg>(paramTab && TAB_KEYS.includes(paramTab) ? paramTab : 'series');
  const [expanded, setExpanded] = useState<Set<string>>(() => (openKey ? new Set([openKey]) : new Set()));

  const { data, isLoading, error } = useQuery({
    queryKey: ['segment', tab],
    queryFn: () => api.get<{ by: Seg; groups: Group[] }>(`/api/reports/segments/${tab}`),
  });

  // Deep-link from the dashboard charts: expand + scroll the requested group into view.
  useEffect(() => {
    if (!openKey || isLoading) return;
    setExpanded((s) => new Set(s).add(openKey));
    const el = document.getElementById(`seg-${openKey}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [openKey, isLoading, tab]);

  const [profile, setProfile] = useState<{ id: number; name: string } | null>(null);

  const switchTab = (t: Seg) => {
    setTab(t);
    setExpanded(new Set());
    setParams({}, { replace: true }); // drop any deep-link params on manual navigation
  };
  const toggle = (key: string) =>
    setExpanded((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });

  return (
    <div className="w-full">
      <h1 className="text-xl font-bold tracking-tight m-0">Segments</h1>
      <p className="text-sm text-text-muted mt-1 mb-4">The book sliced by series, customer, district, agent, staff, and funding channel (Locker Deposit = locker deposits · Dhanamfin App = app-sourced NCDs). These match the Dashboard channel tiles. Click a row's <span className="font-mono">+</span> to see its individual investments (including redeemed ones).</p>
      <div className="flex gap-1 mb-4 border-b border-border">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => switchTab(t.key)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === t.key ? 'border-primary text-primary font-semibold' : 'border-transparent text-text-muted hover:text-text'}`}>
            {t.label}
          </button>
        ))}
      </div>
      {error ? <div className="text-danger">Failed to load this segment.</div> : isLoading ? <div className="text-text-muted">Loading…</div> : (
        <DataTable
          columns={groupColumns(tab, expanded, toggle)}
          rows={data!.groups}
          rowKey={(g) => g.key}
          rowId={(g) => `seg-${g.key}`}
          // Series-wise defaults to series-number descending (NCD 27 → 10);
          // the numeric-aware sort reads the embedded number in the code.
          defaultSort={tab === 'series' ? { key: 'label', dir: 'desc' } : { key: 'outstanding', dir: 'desc' }}
          empty="No data."
          renderExpanded={(g) => (expanded.has(g.key) ? <ChildTable tab={tab} rows={g.children} onPickCustomer={(c) => setProfile({ id: c.customer_id, name: c.customer })} /> : null)}
        />
      )}
      {profile && <CustomerProfileModal id={profile.id} name={profile.name} onClose={() => setProfile(null)} />}
    </div>
  );
}

function groupColumns(tab: Seg, expanded: Set<string>, toggle: (k: string) => void): Column<Group>[] {
  // Expander shows only the label; tab-specific fields (status / customer id)
  // get their own columns below so they're independently sortable/filterable.
  const expander = (g: Group) => (
    <button onClick={() => toggle(g.key)} className="inline-flex items-center gap-2 text-left hover:text-primary">
      <span className="w-4 h-4 inline-flex items-center justify-center rounded border border-border-strong text-[11px] leading-none text-text-muted">
        {expanded.has(g.key) ? '−' : '+'}
      </span>
      <span className="font-medium">{g.label}</span>
    </button>
  );
  const investors: Column<Group> = { key: 'investors', header: 'Investors', align: 'right', value: (g) => g.investors };
  const ncds: Column<Group> = { key: 'investments', header: 'NCDs', align: 'right', value: (g) => g.investments };
  const outstanding: Column<Group> = { key: 'outstanding', header: 'Outstanding', align: 'right', value: (g) => Number(g.outstanding), render: (g) => <span className="mono">{formatINR(g.outstanding)}</span> };

  if (tab === 'series') {
    return [
      { key: 'label', header: 'Series', value: (g) => g.label, render: expander },
      { key: 'status', header: 'Allotment status', value: (g) => g.sublabel ?? '',
        render: (g) => <span className="text-xs rounded px-1.5 py-0.5 bg-bg">{g.sublabel ?? '—'}</span> },
      { key: 'window', header: 'Window', sortable: false, value: (g) => g.window_from ?? '',
        render: (g) => <span className="text-xs whitespace-nowrap">{fmtWindow(g.window_from, g.window_to)}</span> },
      investors, ncds,
      { key: 'issued', header: 'Issued', align: 'right', value: (g) => Number(g.issued ?? 0), render: (g) => <span className="mono">{formatINR(g.issued ?? 0)}</span> },
      { key: 'redeemed', header: 'Redeemed', align: 'right', value: (g) => Number(g.redeemed ?? 0),
        render: (g) => Number(g.redeemed ?? 0) > 0 ? <span className="mono text-danger">{formatINR(g.redeemed ?? 0)}</span> : <span className="text-text-muted">—</span> },
      outstanding,
    ];
  }
  if (tab === 'customer') {
    return [
      { key: 'label', header: 'Customer', value: (g) => g.label, render: expander },
      { key: 'code', header: 'Customer ID', value: (g) => g.sublabel ?? '', tdClassName: 'font-mono text-xs', render: (g) => g.sublabel ?? '—' },
      { key: 'district', header: 'District', value: (g) => g.district ?? '', render: (g) => g.district ?? '—' },
      { key: 'sourced_by', header: 'Sourced by', value: (g) => g.sourced_by ?? '', render: (g) => g.sourced_by ?? '—' },
      ncds, outstanding,
    ];
  }
  if (tab === 'lockerhub' || tab === 'dhanamfin') {
    // Funding-channel views group by series (only that channel's investments).
    // Issued + Redeemed reconcile with the Dashboard tile: Issued (money in) =
    // Outstanding + Redeemed.
    return [
      { key: 'label', header: 'Series', value: (g) => g.label, render: expander },
      { key: 'status', header: 'Allotment status', value: (g) => g.sublabel ?? '',
        render: (g) => <span className="text-xs rounded px-1.5 py-0.5 bg-bg">{g.sublabel ?? '—'}</span> },
      investors, ncds,
      { key: 'issued', header: 'Issued', align: 'right', value: (g) => Number(g.issued ?? 0), render: (g) => <span className="mono">{formatINR(g.issued ?? 0)}</span> },
      { key: 'redeemed', header: 'Redeemed', align: 'right', value: (g) => Number(g.redeemed ?? 0),
        render: (g) => Number(g.redeemed ?? 0) > 0 ? <span className="mono text-danger">{formatINR(g.redeemed ?? 0)}</span> : <span className="text-text-muted">—</span> },
      outstanding,
    ];
  }
  const label = tab === 'district' ? 'District' : tab === 'branch' ? 'Branch' : tab === 'agent' ? 'Agent' : 'Staff';
  return [{ key: 'label', header: label, value: (g) => g.label, render: expander }, investors, ncds, outstanding];
}

function ChildTable({ tab, rows, onPickCustomer }: { tab: Seg; rows: Child[]; onPickCustomer: (c: Child) => void }) {
  // Per-tab detail columns (besides Redeemed + Outstanding, always last two,
  // right-aligned).
  const cols: [string, keyof Child][] =
    tab === 'customer' ? [['Series', 'series_code'], ['App no.', 'application_no'], ['Date', 'date_money_received'], ['Status', 'status'], ['Allotted', 'allotment_date']]
    : (tab === 'series' || tab === 'lockerhub' || tab === 'dhanamfin') ? [['Customer', 'customer'], ['App no.', 'application_no'], ['Date', 'date_money_received'], ['Status', 'status'], ['Allotted', 'allotment_date']]
    // Branch-wise answers "who brought this, and when" — the owner's ask.
    : tab === 'branch' ? [['Customer', 'customer'], ['Brought by', 'sourced_by'], ['Date', 'date_money_received'], ['App no.', 'application_no'], ['Status', 'status']]
    : [['Customer', 'customer'], ['Series', 'series_code'], ['App no.', 'application_no'], ['Date', 'date_money_received'], ['Status', 'status']];
  return (
    <div className="bg-bg/60 px-4 py-2 border-l-2 border-primary/30">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-text-muted">
            {cols.map(([h]) => <th key={h} className="py-1 pr-4 font-medium">{h}</th>)}
            <th className="py-1 pr-4 text-right font-medium">Redeemed</th>
            <th className="py-1 text-right font-medium">Outstanding</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-border/60">
              {cols.map(([, k]) => (
                <td key={k} className="py-1 pr-4">
                  {k === 'application_no' ? <span className="font-mono">{r[k]}</span>
                    : k === 'customer' ? (
                      <button onClick={() => onPickCustomer(r)} className="text-primary hover:underline text-left">
                        {r.customer}
                      </button>
                    )
                    : (k === 'allotment_date' || k === 'date_money_received') ? (r[k] ? String(r[k]).slice(0, 10) : '—')
                    : (r[k] ?? '—')}
                </td>
              ))}
              <td className="py-1 pr-4 text-right mono">{Number(r.redeemed) > 0 ? <span className="text-danger">{formatINR(r.redeemed)}</span> : <span className="text-text-muted">—</span>}</td>
              <td className="py-1 text-right mono">{formatINR(r.outstanding)}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={cols.length + 2} className="py-2 text-center text-text-muted">No investments.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
